import { computed, nextTick, ref } from 'vue'
import { isArray } from '@/global/utils/utils'
import { notify } from '@/global/services/notification/notificationBus'
import { useTableService } from '@/global/composables/table/useTableService'
import { useTaskCodebooks } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskCodebooks'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  BOOK_TASKS_TABLE_KEY,
  BOOK_TASKS_UI_VIEW,
  BOOK_TRANSLATION_NAMESPACE as NS,
  TASKS_ENDPOINT
} from '@/modules/road-maintenance-module/winter-service-v2/book/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: TASKS_ENDPOINT
  }
}

/**
 * Picking the tasks a new book is made of.
 *
 * The screen the button opens is the task catalogue with checkboxes on the rows - only
 * tasks may go into a book, so this reads the tasks listing itself rather than anything
 * of its own. Confirming hands the ticked ids over to the title dialog, which is the
 * only thing the dispatcher still has to type.
 */
export function useBookTasks (options) {
  const { trans, onCancel, onConfirm } = options || {}

  const {
    taskTypeItems,
    taskCategoryItems,
    cleaningTypeItems
  } = useTaskCodebooks({ translate: trans })

  const selectedTasks = ref([])
  const shouldResetSelections = ref(false)

  const selectedTaskIds = computed(() => selectedTasks.value
    .map(task => Number(task?.id))
    .filter(id => !!id)
  )

  const onSelectionsChange = (event) => {
    selectedTasks.value = isArray(event?.data) ? event.data : []
  }

  const resetSelection = async () => {
    selectedTasks.value = []
    // DataTable clears its own selection state when it sees this turn truthy, so the
    // flag has to be lowered again - but only after the watcher it drives has run.
    shouldResetSelections.value = true
    await nextTick()
    shouldResetSelections.value = false
  }

  const cancel = async () => {
    await resetSelection()
    onCancel?.()
  }

  const confirm = () => {
    if (!selectedTaskIds.value.length) {
      notify(trans(`${NS}.select_at_least_one_task`))
      return
    }

    onConfirm?.(selectedTaskIds.value)
  }

  // The listing reports every codebook as its own object, so the columns read their
  // label off those. A task the catalogue left empty says so rather than showing blank.
  const codebookCell = (key) => ({
    [key]: {
      custom_display_value: (data) => data?.[key]?.description || data?.[key]?.name || '-'
    }
  })

  const cellsConfig = computed(() => ({
    ...codebookCell('type'),
    ...codebookCell('category'),
    ...codebookCell('surface_type'),
    ...codebookCell('cleaning_type'),
    ...codebookCell('vehicle_type'),
    ...codebookCell('drive_type'),
    ...codebookCell('spread_type'),
    ...codebookCell('throw_side'),
    ...codebookCell('ownership'),
    spread_quantity: {
      custom_display_value: (data) => data?.spread_quantity ?? '-'
    }
  }))

  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: trans(`${NS}.task_type`),
      key: 'type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.task_category`),
      key: 'category',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.surface_type`),
      key: 'surface_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.cleaning_type`),
      key: 'cleaning_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.vehicle_type`),
      key: 'vehicle_type',
      sortable: true
    },
    {
      label: trans(`${NS}.drive_type`),
      key: 'drive_type',
      sortable: true
    },
    {
      label: trans(`${NS}.spread_type`),
      key: 'spread_type',
      sortable: true
    },
    {
      label: trans(`${NS}.spread_quantity`),
      key: 'spread_quantity',
      sortable: true
    },
    {
      label: trans(`${NS}.throw_side`),
      key: 'throw_side',
      sortable: true
    },
    {
      label: trans(`${NS}.contractor`),
      key: 'ownership',
      sortable: true
    }
  ]))

  // Only what the tasks listing endpoint understands. The codebook lists are already
  // fetched, so these are fixed pickers rather than remote lookups.
  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans(`${NS}.task_name`)
      },
      {
        key: 'type_id',
        label: trans(`${NS}.task_type`),
        type: 'autocomplete',
        items: taskTypeItems.value,
        autocomplete_options: {
          allowSearch: false
        }
      },
      {
        key: 'category_id',
        label: trans(`${NS}.task_category`),
        type: 'autocomplete',
        items: taskCategoryItems.value,
        autocomplete_options: {
          allowSearch: false
        }
      },
      {
        key: 'cleaning_type_id',
        label: trans(`${NS}.cleaning_type`),
        type: 'autocomplete',
        items: cleaningTypeItems.value,
        autocomplete_options: {
          allowSearch: false
        }
      }
    ]
  }))

  const additionalButtons = computed(() => ([
    {
      // Leaving the picking mode without making a book.
      type: 'cancel',
      label: trans('base.cancel'),
      click: cancel
    },
    {
      // Green from the start: the rows can be ticked the moment this screen opens, so
      // this button is always the confirm rather than a mode switch.
      type: 'green-save',
      label: trans(`${NS}.create_book`),
      click: confirm
    }
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.winter_service_book')
      },
      {
        text: trans(`${NS}.new_book`),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: BOOK_TASKS_TABLE_KEY,
      uiView: BOOK_TASKS_UI_VIEW,
      apiConfig: API_CONFIG,
      // No `rowDblClick`: a row is here to be ticked, and opening the task would throw the
      // whole selection away.
      search_and_buttons_config: {
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        additional_buttons: additionalButtons.value,
        showNewBtn: false
      },
      rowsSelection: {
        showCheckboxes: true,
        multiple: true,
        resetSelections: shouldResetSelections.value,
        // Keyed by the task's id rather than the row index, so a selection survives
        // paging and filtering.
        rememberSelectionByDataProperty: 'id',
        selectionsChange: onSelectionsChange
      },
      tableConfig: {
        // Nothing to do to a single row here - the whole screen is about the selection.
        displayActions: false,
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    selectedTaskIds,
    resetSelection
  }
}
